import { useQuery } from '@apollo/client';
import {
  Center,
  Flex,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { TimestampCell } from '@investigator/shared/components';
import { AppConfigurationsContext } from '@investigator/shared/providers';
import { i18n } from '@investigator/shared/localization';

import { AccordionPanel } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { DetectionAlertItem } from '@investigator/features/entities/context';
import moment from 'moment';
import { useContext, useState } from 'react';
import { useHumioToken } from '@investigator/shared/providers';
import { SanitizedAnomalyAlert } from '../..';
import {
  DetailsGrid,
  DetailsGridRow,
} from '@investigator/features/detections/components';
import {
  AlertWithSessionSummary,
  IQueryAlertsPaginated,
  QUERY_ALERTS_ANOMALY_SUMMARY,
  SessionSummaryData,
} from '@investigator/features/detections/queries';
import { buildAlertIdQuery } from '../../utils/buildAlertIdQuery';
import { InvestigateLogsTableModal } from '../InvestigateLogsTableModal';
import { LogscaleDetails } from '../LogscaleDetails';
import { buildSearchQueryString } from '../LogscaleDetails/utils/buildSearchQueryString';
import { LogscaleMenuButton } from '../LogscaleMenuButton';
import { SessionSummary } from '../SessionSummary';
import { useDetectionContext } from '@investigator/features/detections/context';
const { alerts, detection } = i18n.pages.detections;
interface AnomalyButtonsColProps {
  item: SanitizedAnomalyAlert &
    Pick<DetectionAlertItem, 'alert_name' | 'severity' | 'is_custom_severity'>;
  logScaleQuery?: string;
  showContent?: boolean;
  loading?: boolean;
}

export const AnomalyButtonsCol = ({
  item,
  logScaleQuery,
  showContent,
}: AnomalyButtonsColProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const observed =
    item.alert_timestamp && item.alert_timestamp.observed != null
      ? item.alert_timestamp.observed
      : 0;
  const formattedTime = moment.unix(observed).format();
  const start = moment(formattedTime).subtract(1, 'hours').unix();
  const end = moment(formattedTime).add(24, 'hours').unix();

  const searchQueryString = buildSearchQueryString({
    item: {
      alert_name: item.alert_info?.alert_name ?? '',
      alert_info: {
        alert_type: item.alert_info?.alert_type ?? '',
        alert_name: item.alert_info?.alert_name ?? '',
      },
      alert_timestamp: { observed },
      alert_entity: {
        entity_name: item.alert_entity?.entity_name ?? '',
        entity_type: item.alert_entity?.entity_type ?? '',
      },
      severity: item.severity ?? 0,
      is_custom_severity: item.is_custom_severity ?? false,
      uid: item.uid,
      first_seen: 0,
      last_seen: 0,
    },
    logScaleQuery,
    start,
    end,
  });

  return (
    <Flex flexGrow={1}>
      <LogscaleMenuButton
        searchQueryString={searchQueryString}
        investigateModalOnOpen={onOpen}
        showContent={showContent}
      />
      <InvestigateLogsTableModal
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        alertsName={item.alert_info?.alert_name ?? ''}
        alertsUid={item.uid}
        alertsTimestamp={formattedTime}
        alertsTenant={item.tenant}
      />
    </Flex>
  );
};
interface AlertRowAnomalyProps {
  item: SanitizedAnomalyAlert &
    Pick<DetectionAlertItem, 'alert_name' | 'severity' | 'is_custom_severity'>;
  logScaleQuery?: string;
  showContent?: boolean;
}

export const AlertRowAnomaly = ({
  item,
  logScaleQuery,
  showContent,
}: AlertRowAnomalyProps) => {
  const observed =
    item.alert_timestamp && item.alert_timestamp.observed != null
      ? item.alert_timestamp.observed
      : 0;

  return (
    <HStack spacing={4}>
      <VStack alignItems="start">
        <TimestampCell timestamp={observed} isFromNow={false} />
        <HStack color="text.200">
          <Text textStyle="body-md">{item?.alert_info?.alert_name ?? ''}</Text>
          {item?.alert_entity ? (
            <>
              <Text textStyle="body-md">|</Text>
              <Text textStyle="body-md">{item?.alert_entity?.entity_name}</Text>
            </>
          ) : null}
        </HStack>
      </VStack>
      <AnomalyButtonsCol
        item={item}
        logScaleQuery={logScaleQuery}
        showContent={showContent}
      />
    </HStack>
  );
};
interface AlertRowSplitAnomalyProps {
  item: SanitizedAnomalyAlert;
  loading: boolean | undefined;
  isGPTPrivateDataEnabled: boolean;
  logScaleQuery?: string;
}

export const AlertRowSplitAnomaly = ({
  item,
  loading,
  isGPTPrivateDataEnabled,
  logScaleQuery,
}: AlertRowSplitAnomalyProps) => {
  const { appConfigurations } = useContext(AppConfigurationsContext);
  const { token: sessionToken } = useHumioToken();
  const { detectionData } = useDetectionContext();
  const [sessionSummaryData, setSessionSummaryData] = useState<
    SessionSummaryData | undefined
  >(undefined);

  const observedSplit =
    item.alert_timestamp && item.alert_timestamp.observed != null
      ? item.alert_timestamp.observed
      : 0;
  const time = moment.unix(observedSplit);
  const formattedTime = time.format();

  const isLLMSummary = appConfigurations?.feature_flags.llm_summary || false;

  // Fetches session_summary data. Query is skipped when GPT Private Data is disabled to avoid privilege errors.
  const query = QUERY_ALERTS_ANOMALY_SUMMARY;
  const noFilterVariables = {
    query: buildAlertIdQuery(item.alert_id),
    log_search_client_session_id: sessionToken || '',
  };
  const { loading: sessionSummaryLoading } = useQuery<IQueryAlertsPaginated>(
    query,
    {
      skip:
        !detectionData ||
        !detectionData?.tenant_info?.tenant_id ||
        !detectionData?.alert_entity?.entity_id ||
        !isLLMSummary ||
        !isGPTPrivateDataEnabled,
      variables: noFilterVariables,
      onCompleted: (data) => {
        const sessionSummary = (
          data?.queryAlertsPaginated?.alerts?.[0] as AlertWithSessionSummary
        )?.session_summary;
        setSessionSummaryData(sessionSummary || undefined);
      },
      onError: (error) => {
        console.error(error);
      },
    },
  );

  if (loading) {
    return (
      <Center w="100%">
        <GraphQLReqStatus loading={loading} />
      </Center>
    );
  }

  return (
    <VStack mb={2} w="100%" alignItems="start">
      <LogscaleDetails
        alertsUid={item?.uid}
        alertsName={item?.alert_info?.alert_name ?? ''}
        alertsTimestamp={formattedTime}
        alertsTenant={item?.tenant}
        logScaleQuery={logScaleQuery}
        alertType="anomaly"
        childrenBelowHeader={
          <AccordionPanel
            title={detection.alertDetails}
            data-testid="anomaly-alerts-wrapper"
            reduceMotion
          >
            <DetailsGrid>
              <DetailsGridRow title={alerts.name}>
                <Text overflowWrap="anywhere">
                  {item?.alert_info?.alert_name}
                </Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.timestamp}>
                <Text>{formattedTime}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.uid}>
                <Text>{item?.uid}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.usecase}>
                <Text overflowWrap="anywhere">{item?.usecase}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.anomalyType}>
                <Text overflowWrap="anywhere">{item?.anomaly_type}</Text>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.sourceIp}>
                <VStack alignItems="start" spacing={1}>
                  {item?.source_entities?.map((source, idx) => (
                    <Text key={idx}>{source?.entity_name}</Text>
                  ))}
                </VStack>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.destinationIp}>
                <VStack alignItems="start" spacing={1}>
                  {item?.destination_entities?.map((dest, idx) => (
                    <Text key={idx}>{dest?.entity_name}</Text>
                  ))}
                </VStack>
              </DetailsGridRow>
              <DetailsGridRow title={alerts.type}>
                <Text>{item?.alert_info?.alert_type}</Text>
              </DetailsGridRow>
            </DetailsGrid>
            {isLLMSummary && isGPTPrivateDataEnabled && (
              <SessionSummary
                sessionSummaryData={sessionSummaryData}
                loading={sessionSummaryLoading}
              />
            )}
          </AccordionPanel>
        }
      />
    </VStack>
  );
};
